import { useInspectorStore } from "@/stores/inspectorStore";

/**
 * Navigation utilities for the Inspector tab with URL and store synchronization
 */

// Type for tool parameters that can be encoded in URLs
export type InspectorToolParams = Record<string, string>;

/**
 * Encode parameters as base64 JSON for URL safety
 */
export function encodeParams(params: InspectorToolParams): string {
  try {
    const json = JSON.stringify(params);
    return btoa(json);
  } catch {
    return "";
  }
}

/**
 * Decode parameters from base64 JSON
 */
export function decodeParams(encoded: string): InspectorToolParams {
  try {
    const json = atob(encoded);
    const params = JSON.parse(json);
    return typeof params === "object" && params !== null ? params : {};
  } catch {
    return {};
  }
}

/**
 * Generate a shareable URL for the current inspector state
 */
export function generateInspectorShareUrl(
  projectId: string,
  toolName: string,
  params?: InspectorToolParams,
): string {
  const baseUrl = window.location.origin;
  const searchParams = new URLSearchParams();

  searchParams.set("tab", "inspector");
  searchParams.set("selectedTool", toolName);

  if (params && Object.keys(params).length > 0) {
    searchParams.set("params", encodeParams(params));
  }

  return `${baseUrl}/projects/${projectId}?${searchParams.toString()}`;
}

/**
 * Apply parameters from URL to the store
 * Used when navigating with deep-linked parameters
 */
export function applyToolUrlParamsToStore(
  toolName: string,
  encodedParams?: string,
) {
  if (!encodedParams) {
    return;
  }

  const params = decodeParams(encodedParams);
  const { updateToolParameters } = useInspectorStore.getState();

  // Apply parameters without triggering invalidation (this is navigation, not user edit)
  updateToolParameters(toolName, params);
}

/**
 * Generate search parameters for inspector navigation
 */
export function createInspectorSearchParams(
  toolName: string,
  params?: InspectorToolParams,
) {
  const searchParams: {
    tab: "inspector";
    selectedTool: string;
    params?: string;
    selectedPrompt?: string;
    promptParams?: string;
  } = {
    tab: "inspector",
    selectedTool: toolName,
  };

  if (params && Object.keys(params).length > 0) {
    searchParams.params = encodeParams(params);
  }

  return searchParams;
}

export function createPromptSearchParams(
  promptName: string,
  params?: InspectorToolParams,
) {
  const searchParams: {
    tab: "inspector";
    selectedPrompt: string;
    promptParams?: string;
    selectedTool?: string;
    params?: string;
  } = {
    tab: "inspector",
    selectedPrompt: promptName,
  };

  if (params && Object.keys(params).length > 0) {
    searchParams.promptParams = encodeParams(params);
  }

  return searchParams;
}

export function applyPromptUrlParamsToStore(
  promptName: string,
  encodedParams?: string,
) {
  if (!encodedParams) {
    return;
  }

  const params = decodeParams(encodedParams);
  const { updatePromptArguments } = useInspectorStore.getState();
  updatePromptArguments(promptName, params);
}
